import React from 'react';
import { FaBitcoin, FaRegSmile, FaBolt, FaShieldAlt } from 'react-icons/fa';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import { Tooltip } from 'react-tooltip';
import { getMarketRiskLabel } from './marketRegimeLabels';

const getSentimentColor = (score) => {
  if (score < 20) return '#e53935';
  if (score < 40) return '#ffb300';
  if (score < 60) return '#ffd600';
  if (score < 80) return '#43a047';
  return '#1976d2';
};

const formatValue = (value, suffix = '') => (value == null || Number.isNaN(Number(value)) ? 'N/A' : `${Number(value).toFixed(2)}${suffix}`);

const renderSparkline = (values, color) => {
  if (!Array.isArray(values) || values.length < 2) {
    return <span className="metric-na">Historique indisponible</span>;
  }

  return (
    <Sparklines data={values} width={60} height={18} margin={4}>
      <SparklinesLine color={color} style={{ strokeWidth: 2, fill: 'none' }} />
    </Sparklines>
  );
};

const MacroMetricsGrid = ({ metrics }) => (
  <div className="macro-metrics-grid">
    {/* Carte 1 — Dominance */}
    <div className="metric-card">
      <div className="metric-title"><FaBitcoin color="#f2a900" style={{marginRight:6}}/>Dominance</div>
      <div className="metric-value-stack">
        <div className="metric-value-lg">
          <span data-tooltip-id="btc-dom-tooltip">BTC {formatValue(metrics.btcDom, '%')}</span>
        </div>
        <div className="metric-inline-row">
          <span data-tooltip-id="eth-dom-tooltip">ETH {formatValue(metrics.ethDom, '%')}</span>
          <span className="metric-pill">Rotation relative</span>
        </div>
        <Tooltip id="btc-dom-tooltip" place="top" content="Part de marché Bitcoin" />
        <Tooltip id="eth-dom-tooltip" place="top" content="Part de marché Ethereum" />
      </div>
      <div className="metric-sub">Var 7j: {formatValue(metrics.btcVar7d, '%')} / {formatValue(metrics.ethVar7d, '%')}</div>
      <div className="metric-sparkline">
        {renderSparkline(metrics.btcDomHistory, '#f2a900')}
      </div>
    </div>
    {/* Carte 2 — Sentiment */}
    <div className="metric-card">
      <div className="metric-title"><FaRegSmile color="#4caf50" style={{marginRight:6}}/>Sentiment</div>
      <div className="metric-value-stack">
        <div className="metric-inline-row">
          <span className="metric-value-lg" data-tooltip-id="sentiment-tooltip">{metrics.sentimentLabel || 'N/A'}</span>
          <span className="metric-pill">{metrics.sentiment != null ? `${Math.round(Number(metrics.sentiment))}/100` : 'N/A'}</span>
        </div>
        <Tooltip id="sentiment-tooltip" place="top" content="Indice Fear & Greed du marché crypto" />
        {metrics.sentiment != null ? (
          <span className="metric-gauge" style={{
            display: 'inline-block',
            width: 88,
            height: 10,
            borderRadius: 6,
            background: 'rgba(23, 50, 60, 0.12)',
            position: 'relative',
          }}>
            <span style={{
              position: 'absolute',
              left: `${Math.max(0, Math.min(100, Number(metrics.sentiment)))}%`,
              top: 0,
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: getSentimentColor(Number(metrics.sentiment)),
              transform: 'translateX(-50%)',
              border: '2px solid #fff',
            }} />
          </span>
        ) : <span className="metric-na">N/A</span>}
      </div>
      <div className="metric-sub">
        Évolution :
        {renderSparkline(metrics.sentimentHistory, '#4caf50')}
      </div>
    </div>
    {/* Carte 3 — Momentum Global */}
    <div className={`metric-card${metrics.momentum == null ? ' metric-card-disabled' : ''}`}>
      <div className="metric-title"><FaBolt color="#7b2ff2" style={{marginRight:6}}/>Momentum Global</div>
      <div className="metric-value-stack">
        {metrics.momentum != null ? (
          <span className="metric-value-lg" data-tooltip-id="momentum-tooltip">{formatValue(metrics.momentum)}</span>
        ) : <span className="metric-na">N/A</span>}
        <Tooltip id="momentum-tooltip" place="top" content="Score de momentum global (0-100)" />
        <span className="metric-pill">Impulsion globale</span>
      </div>
      <div className="metric-sub">Var: {metrics.momentumVar != null ? formatValue(metrics.momentumVar) : '--'}</div>
      <div className="metric-sparkline">
        {renderSparkline(metrics.momentumHistory, '#7b2ff2')}
      </div>
    </div>
    {/* Carte 4 — Risk Environment */}
    <div className={`metric-card${metrics.volatility == null ? ' metric-card-disabled' : ''}`}>
      <div className="metric-title"><FaShieldAlt color="#ffb300" style={{marginRight:6}}/>Risk Environment</div>
      <div className="metric-value-stack">
        <div className="metric-inline-row">
          <span className="metric-value-lg">Vol {metrics.volatility != null ? (
            <span data-tooltip-id="volatility-tooltip">{formatValue(metrics.volatility, '%')}</span>
          ) : <span className="metric-na">N/A</span>}</span>
          <Tooltip id="volatility-tooltip" place="top" content="Volatilité moyenne du marché" />
        </div>
        <div className="metric-inline-row">
          <span>Risk-on/off</span>
          {metrics.riskOnOff != null ? (
            <span className="metric-badge-enabled" data-tooltip-id="risk-tooltip">{getMarketRiskLabel(metrics.riskOnOff)}</span>
          ) : <span className="metric-badge-disabled">N/A</span>}
        </div>
        <Tooltip id="risk-tooltip" place="top" content="Environnement risk-on (favorable) ou risk-off (défensif)" />
      </div>
      <div className="metric-sub">Breadth: {metrics.breadthUp ?? 'N/A'}/{metrics.breadthTotal ?? 'N/A'}</div>
    </div>
  </div>
);

export default MacroMetricsGrid;
